import { CancelOutlined, EditOutlined, RefreshOutlined } from '@mui/icons-material';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  Paper,
  Snackbar,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import { ResponsiveBar } from '@nivo/bar';
import { ResponsiveLine } from '@nivo/line';
import moment from 'moment';
import { formatPct, TERMS } from '../../helpers/kpiDefinitions';
import { FC, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import Header from '../../components/Header';
import { FilterContext } from '../../contexts/FilterContext';
import { api } from '../../helpers/apiUtils';
import { tokens } from '../../theme';

const TITLE_MAX = 60;
const BODY_MAX = 120;

interface Overview {
  total: number;
  active: number;
  active_registered: number;
  by_email: number;
  anonymous: number;
  linked: number;
  with_isin: number;
  distinct_isins: number;
  revoked: number;
}
interface TopAsset {
  asset_name: string;
  isin: string;
  subscribers: number;
}
interface GrowthPoint {
  day: string;
  new_subscribers: number;
}
interface Mailing {
  id: number;
  send_date: string;
  source_type: 'vnr' | 'plx' | 'webinar' | 'webinar_followup';
  source_ref_id: number;
  source_name: string | null;
  isin: string | null;
  title: string;
  body: string;
  image_url: string | null;
  click_url: string;
  status: 'draft' | 'scheduled' | 'sent' | 'cancelled' | 'failed';
  ai_status: string | null;
  it_campaign_id: number | null;
  scheduled_send_at: string | null;
  sent_at: string | null;
  error_message: string | null;
  web_push_send: number | null;
  web_push_click: number | null;
  ctr: number | null;
  conversions: number;
  revenue: number;
}

interface CampaignSummary {
  campaigns: number;
  sends: number;
  clicks: number;
  conversions: number;
  revenue_vnr: number;
  revenue_cpa: number;
  revenue: number;
  ctr: number | null;
  conv_rate: number | null;
  rev_per_conv: number | null;
  etkp: number | null;
}

const eurFmt = (v: number) =>
  new Intl.NumberFormat('de-DE', {
    style: 'currency',
    currency: 'EUR',
    maximumFractionDigits: v >= 100 ? 0 : 2,
  }).format(v || 0);

const STATUS_COLOR: Record<
  Mailing['status'],
  'default' | 'info' | 'success' | 'error' | 'warning'
> = {
  draft: 'default',
  scheduled: 'info',
  sent: 'success',
  cancelled: 'error',
  failed: 'error',
};

const fmt = (n: number | null | undefined) => (n ?? 0).toLocaleString('de-DE');
const pct = (v: number | null | undefined) =>
  v == null ? '–' : `${(v * 100).toFixed(1).replace('.', ',')} %`;

// Multiple webinar-related pushes can share a send_date; label + sort by
// scheduled_send_at (with fallback) so they don't collapse onto one bar.
const mailingLabel = (m: Mailing) =>
  moment(m.scheduled_send_at || m.send_date).format('DD.MM HH:mm');
const mailingSortKey = (m: Mailing) => m.scheduled_send_at || m.send_date;

const WebPushDashboard: FC = () => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const { filterState } = useContext(FilterContext)!;

  const startStr = useMemo(
    () =>
      moment(filterState.startDate ?? moment().subtract(30, 'days').toDate()).format('YYYY-MM-DD'),
    [filterState.startDate],
  );
  const endStr = useMemo(
    () => moment(filterState.endDate ?? new Date()).format('YYYY-MM-DD'),
    [filterState.endDate],
  );

  const [overview, setOverview] = useState<Overview | null>(null);
  const [topAssets, setTopAssets] = useState<TopAsset[]>([]);
  const [growth, setGrowth] = useState<GrowthPoint[]>([]);
  const [mailings, setMailings] = useState<Mailing[]>([]);
  // Summen aus dem Backend ueber die ganze gefilterte Menge (Phase 3c) — Prozentwerte sind Prozent.
  const [summary, setSummary] = useState<CampaignSummary | null>(null);
  const [paused, setPaused] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  // edit modal
  const [editRow, setEditRow] = useState<Mailing | null>(null);
  const [form, setForm] = useState({ title: '', body: '', image_url: '', click_url: '' });

  const load = useCallback(
    async (signal?: { cancelled: boolean }) => {
      setLoading(true);
      setError(null);
      try {
        const [subRes, mailRes, setRes] = await Promise.all([
          api.get('/api/web-push/subscriber-stats', {
            params: { start_date: startStr, end_date: endStr },
          }),
          api.get('/api/web-push/mailings', {
            params: { start_date: startStr, end_date: endStr, limit: 365 },
          }),
          api.get('/api/web-push/settings'),
        ]);
        if (signal?.cancelled) return;
        setOverview(subRes.data.data.overview);
        setTopAssets(subRes.data.data.top_assets);
        setGrowth(subRes.data.data.growth);
        setMailings(mailRes.data.data);
        setSummary(mailRes.data.summary ?? null);
        setPaused(Boolean(setRes.data.data.paused));
      } catch (e: any) {
        if (!signal?.cancelled)
          setError(e?.response?.data?.error || e?.message || 'Fehler beim Laden');
      } finally {
        if (!signal?.cancelled) setLoading(false);
      }
    },
    [startStr, endStr],
  );

  useEffect(() => {
    const signal = { cancelled: false };
    load(signal);
    return () => {
      signal.cancelled = true;
    };
  }, [load]);

  // Campaign KPIs over the selected window (scheduled/sent only).
  // `mailings` is already windowed server-side by start/end (see load()), so the
  // campaign KPIs/chart only need the status filter — no client-side date compare
  // (send_date arrives as an ISO timestamp, which broke a YYYY-MM-DD string match).
  const windowMailings = useMemo(
    () => mailings.filter((m) => m.status === 'scheduled' || m.status === 'sent'),
    [mailings],
  );
  // KPIs kommen aus dem Backend-Summary (gesamte Menge, Geld getrennt nach VNR-CPL und CPA-Wert);
  // die Zeilen dienen nur noch der Tabelle und den Charts.
  const campaignKpi: CampaignSummary = summary ?? {
    campaigns: windowMailings.length,
    sends: 0,
    clicks: 0,
    conversions: 0,
    revenue_vnr: 0,
    revenue_cpa: 0,
    revenue: 0,
    ctr: null,
    conv_rate: null,
    rev_per_conv: null,
    etkp: null,
  };

  const growthSeries = useMemo(
    () => [
      {
        id: 'Neue Abonnenten',
        data: growth.map((g) => ({ x: moment(g.day).format('DD.MM'), y: g.new_subscribers })),
      },
    ],
    [growth],
  );
  const sendBarData = useMemo(
    () =>
      [...windowMailings]
        .sort((a, b) => mailingSortKey(a).localeCompare(mailingSortKey(b)))
        .map((m) => ({
          date: mailingLabel(m),
          Sends: m.web_push_send || 0,
          Klicks: m.web_push_click || 0,
        })),
    [windowMailings],
  );
  const revenueBarData = useMemo(
    () =>
      [...windowMailings]
        .sort((a, b) => mailingSortKey(a).localeCompare(mailingSortKey(b)))
        .map((m) => {
          const rev = Number(m.revenue) || 0;
          const sends = m.web_push_send || 0;
          return {
            date: mailingLabel(m),
            type: m.source_type.toUpperCase(),
            'CPA-Wert / CPL': Number(rev.toFixed(2)),
            ETKP: sends > 0 ? Number(((rev / sends) * 1000).toFixed(2)) : 0,
          };
        }),
    [windowMailings],
  );
  // Cumulative revenue line — answers "how much has the channel earned so far?"
  const revenueCumulative = useMemo(() => {
    let acc = 0;
    const points = [...windowMailings]
      .sort((a, b) => mailingSortKey(a).localeCompare(mailingSortKey(b)))
      .map((m) => {
        acc += Number(m.revenue) || 0;
        return { x: mailingLabel(m), y: Number(acc.toFixed(2)) };
      });
    return [{ id: 'CPA-Wert / CPL kumuliert', data: points }];
  }, [windowMailings]);

  const tooltipBox = (label: string, value: string) => (
    <div
      style={{
        background: '#fff',
        color: '#111',
        padding: '6px 10px',
        borderRadius: 4,
        boxShadow: '0 2px 6px rgba(0,0,0,0.2)',
        fontSize: 13,
      }}
    >
      <strong>{label}</strong>: {value}
    </div>
  );

  const nivoTheme = useMemo(
    () => ({
      text: { fill: colors.grey[100] },
      axis: {
        ticks: { text: { fill: colors.grey[100] }, line: { stroke: colors.grey[700] } },
        legend: { text: { fill: colors.grey[100] } },
      },
      grid: { line: { stroke: colors.grey[800] } },
      tooltip: { container: { background: '#ffffff', color: '#111' } },
      legends: { text: { fill: colors.grey[100] } },
    }),
    [colors],
  );

  const togglePause = async () => {
    setBusy(true);
    try {
      const next = !paused;
      await api.post('/api/web-push/settings', { paused: next });
      setPaused(next);
      setToast(next ? 'Automatik pausiert' : 'Automatik aktiviert');
    } catch (e: any) {
      setError(e?.response?.data?.error || 'Konnte Kill-Switch nicht setzen');
    } finally {
      setBusy(false);
    }
  };

  const openEdit = (m: Mailing) => {
    setEditRow(m);
    setForm({
      title: m.title,
      body: m.body,
      image_url: m.image_url || '',
      click_url: m.click_url,
    });
  };
  const saveEdit = async () => {
    if (!editRow) return;
    setBusy(true);
    try {
      await api.put(`/api/web-push/mailings/${editRow.id}`, form);
      setToast('Entwurf gespeichert');
      setEditRow(null);
      await load();
    } catch (e: any) {
      setError(e?.response?.data?.error || 'Speichern fehlgeschlagen');
    } finally {
      setBusy(false);
    }
  };
  const cancelMailing = async (m: Mailing) => {
    if (!window.confirm(`Mailing #${m.id} (${m.title}) wirklich abbrechen?`)) return;
    setBusy(true);
    try {
      await api.post(`/api/web-push/mailings/${m.id}/cancel`, {});
      setToast('Mailing abgebrochen');
      await load();
    } catch (e: any) {
      setError(e?.response?.data?.error || 'Abbrechen fehlgeschlagen');
    } finally {
      setBusy(false);
    }
  };

  const card = (label: string, value: string, sub?: string, accent?: string) => (
    <Box sx={{ backgroundColor: colors.primary[400], borderRadius: '8px', p: '16px 20px' }}>
      <Typography sx={{ color: colors.grey[100], fontSize: 13, opacity: 0.85 }}>{label}</Typography>
      <Typography variant="h3" fontWeight="bold" sx={{ color: accent || colors.blueAccent[400] }}>
        {value}
      </Typography>
      {sub && (
        <Typography sx={{ color: colors.grey[100], fontSize: 12, opacity: 0.7 }}>{sub}</Typography>
      )}
    </Box>
  );

  return (
    <Box m="20px">
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        flexWrap="wrap"
        gap={2}
      >
        <Header
          title="Web Push"
          subtitle="Iterable Web-Push Kanal — Abonnenten, Mappings & Kampagnen"
        />
        <Box display="flex" alignItems="center" gap={2}>
          <FormControlLabel
            control={
              <Switch checked={!paused} onChange={togglePause} disabled={busy} color="success" />
            }
            label={
              <Typography sx={{ color: colors.grey[100] }}>
                {paused ? 'Automatik AUS' : 'Automatik AN'}
              </Typography>
            }
          />
          <Button
            variant="outlined"
            startIcon={<RefreshOutlined />}
            onClick={() => load()}
            disabled={busy || loading}
          >
            Aktualisieren
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ my: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {loading && !overview ? (
        <Box display="flex" justifyContent="center" p={6}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          {/* Subscriber KPIs */}
          <Typography variant="h5" sx={{ color: colors.grey[100], mt: 2, mb: 1, fontWeight: 600 }}>
            Abonnenten
          </Typography>
          <Box
            display="grid"
            gridTemplateColumns="repeat(auto-fit, minmax(190px, 1fr))"
            gap="16px"
            mb="8px"
          >
            {card(
              'Aktive Abonnenten',
              fmt(overview?.active_registered),
              `${fmt(overview?.total)} gesamt · ${fmt(overview?.revoked)} widerrufen`,
              colors.greenAccent[400],
            )}
            {card(
              'E-Mail-Mapping',
              fmt(overview?.by_email),
              `${pct(
                overview && overview.active_registered
                  ? overview.by_email / overview.active_registered
                  : 0,
              )} bekannt`,
            )}
            {card('Anonym (userId)', fmt(overview?.anonymous), 'nur Browser-Token, keine E-Mail')}
            {card('Asset-Mapping (ISIN)', fmt(overview?.with_isin), 'Token mit Opt-in-Asset')}
            {card('Distinct Assets', fmt(overview?.distinct_isins), 'verschiedene ISINs')}
          </Box>

          {/* Campaign KPIs */}
          <Typography variant="h5" sx={{ color: colors.grey[100], mt: 3, mb: 1, fontWeight: 600 }}>
            Kampagnen ({moment(startStr).format('DD.MM.')}–{moment(endStr).format('DD.MM.YYYY')})
          </Typography>
          <Box
            display="grid"
            gridTemplateColumns="repeat(auto-fit, minmax(190px, 1fr))"
            gap="16px"
            mb="8px"
          >
            {card('Kampagnen', fmt(campaignKpi.campaigns), 'gesendet im Zeitraum')}
            {card('Web-Push Sends', fmt(campaignKpi.sends))}
            {card('Klicks', fmt(campaignKpi.clicks), undefined, colors.greenAccent[400])}
            {card('CTR', formatPct(campaignKpi.ctr), undefined, colors.blueAccent[400])}
            {card(
              'Leads / Bestellungen',
              fmt(campaignKpi.conversions),
              campaignKpi.conv_rate != null
                ? `${formatPct(campaignKpi.conv_rate)} der Klicks`
                : 'VNR-Leads + Anfos aus PLX- und Webinar-Pushes',
              colors.greenAccent[400],
            )}
            {card(
              TERMS.cpaWert.label,
              eurFmt(campaignKpi.revenue_cpa),
              'Anfos aus PLX- und Webinar-Pushes, bewertet mit dem Produkt-CPA — kein Zahlungseingang',
              colors.greenAccent[400],
            )}
            {card(
              'VNR-CPL',
              eurFmt(campaignKpi.revenue_vnr),
              campaignKpi.rev_per_conv != null
                ? `Lead-Vergütung VNR · Ø ${eurFmt(campaignKpi.rev_per_conv)} je Konversion gesamt`
                : 'Lead-Vergütung VNR',
              colors.greenAccent[400],
            )}
            {card(
              'ETKP',
              campaignKpi.etkp != null ? eurFmt(campaignKpi.etkp) : '–',
              'CPA-Wert + VNR-CPL je 1.000 Pushes',
              colors.blueAccent[400],
            )}
          </Box>

          {/* Charts row 1: Subscribers + Sends&Clicks */}
          <Box display="grid" gridTemplateColumns={{ xs: '1fr', md: '1fr 1fr' }} gap="16px" mt={2}>
            <Box
              sx={{ backgroundColor: colors.primary[400], borderRadius: '8px', p: 2, height: 320 }}
            >
              <Typography sx={{ color: colors.grey[100], mb: 1 }}>Abonnenten-Wachstum</Typography>
              <Box height={260}>
                {growthSeries[0].data.length === 0 ? (
                  <Typography sx={{ color: colors.grey[100], opacity: 0.6, p: 2 }}>
                    Keine Daten im Zeitraum.
                  </Typography>
                ) : (
                  <ResponsiveLine
                    data={growthSeries}
                    theme={nivoTheme}
                    margin={{ top: 10, right: 20, bottom: 55, left: 55 }}
                    xScale={{ type: 'point' }}
                    yScale={{ type: 'linear', min: 0, max: 'auto' }}
                    axisBottom={{
                      tickRotation: -45,
                      legend: 'Datum',
                      legendOffset: 45,
                      legendPosition: 'middle',
                    }}
                    axisLeft={{
                      legend: 'Neue Abonnenten',
                      legendOffset: -45,
                      legendPosition: 'middle',
                    }}
                    colors={[colors.greenAccent[400]]}
                    pointSize={7}
                    pointBorderWidth={2}
                    enableArea
                    areaOpacity={0.15}
                    useMesh
                    tooltip={({ point }) =>
                      tooltipBox(
                        `${point.data.xFormatted}`,
                        `${point.data.yFormatted} neue Abonnenten`,
                      )
                    }
                  />
                )}
              </Box>
            </Box>
            <Box
              sx={{ backgroundColor: colors.primary[400], borderRadius: '8px', p: 2, height: 320 }}
            >
              <Typography sx={{ color: colors.grey[100], mb: 1 }}>
                Sends & Klicks je Kampagne
              </Typography>
              <Box height={260}>
                {sendBarData.length === 0 ? (
                  <Typography sx={{ color: colors.grey[100], opacity: 0.6, p: 2 }}>
                    Keine Kampagnen im Zeitraum.
                  </Typography>
                ) : (
                  <ResponsiveBar
                    data={sendBarData}
                    keys={['Sends', 'Klicks']}
                    indexBy="date"
                    groupMode="grouped"
                    theme={nivoTheme}
                    margin={{ top: 10, right: 20, bottom: 55, left: 55 }}
                    padding={0.3}
                    colors={[colors.blueAccent[400], colors.greenAccent[400]]}
                    axisBottom={{
                      tickRotation: -45,
                      legend: 'Datum',
                      legendOffset: 45,
                      legendPosition: 'middle',
                    }}
                    axisLeft={{ legend: 'Anzahl', legendOffset: -45, legendPosition: 'middle' }}
                    labelSkipHeight={14}
                    valueFormat={(v) => fmt(v as number)}
                    tooltip={({ id, value, indexValue }) =>
                      tooltipBox(`${indexValue} · ${id}`, fmt(value))
                    }
                  />
                )}
              </Box>
            </Box>
          </Box>

          {/* Charts row 2: Revenue per campaign + Cumulative revenue */}
          <Box display="grid" gridTemplateColumns={{ xs: '1fr', md: '1fr 1fr' }} gap="16px" mt={2}>
            <Box
              sx={{ backgroundColor: colors.primary[400], borderRadius: '8px', p: 2, height: 320 }}
            >
              <Typography sx={{ color: colors.grey[100], mb: 1 }}>
                CPA-Wert / VNR-CPL & ETKP je Kampagne
              </Typography>
              <Box height={260}>
                {revenueBarData.length === 0 ||
                revenueBarData.every((r) => r['CPA-Wert / CPL'] === 0 && r.ETKP === 0) ? (
                  <Typography sx={{ color: colors.grey[100], opacity: 0.6, p: 2 }}>
                    Noch kein Umsatz im Zeitraum.
                  </Typography>
                ) : (
                  <ResponsiveBar
                    data={revenueBarData}
                    keys={['CPA-Wert / CPL', 'ETKP']}
                    indexBy="date"
                    groupMode="grouped"
                    theme={nivoTheme}
                    margin={{ top: 10, right: 20, bottom: 55, left: 60 }}
                    padding={0.3}
                    colors={[colors.greenAccent[400], colors.blueAccent[400]]}
                    axisBottom={{
                      tickRotation: -45,
                      legend: 'Datum',
                      legendOffset: 45,
                      legendPosition: 'middle',
                    }}
                    axisLeft={{
                      format: (v) => `${v} €`,
                      legend: 'Euro',
                      legendOffset: -50,
                      legendPosition: 'middle',
                    }}
                    valueFormat={(v) => eurFmt(v as number)}
                    labelSkipHeight={14}
                    tooltip={({ id, value, indexValue, data }) =>
                      tooltipBox(
                        `${indexValue} · ${data.type as string} · ${id}`,
                        eurFmt(value as number),
                      )
                    }
                  />
                )}
              </Box>
            </Box>
            <Box
              sx={{ backgroundColor: colors.primary[400], borderRadius: '8px', p: 2, height: 320 }}
            >
              <Typography sx={{ color: colors.grey[100], mb: 1 }}>
                CPA-Wert / VNR-CPL kumuliert · Σ {eurFmt(campaignKpi.revenue)}
              </Typography>
              <Box height={260}>
                {revenueCumulative[0].data.length === 0 ||
                revenueCumulative[0].data.every((p) => p.y === 0) ? (
                  <Typography sx={{ color: colors.grey[100], opacity: 0.6, p: 2 }}>
                    Noch kein Umsatz im Zeitraum.
                  </Typography>
                ) : (
                  <ResponsiveLine
                    data={revenueCumulative}
                    theme={nivoTheme}
                    margin={{ top: 10, right: 20, bottom: 55, left: 60 }}
                    xScale={{ type: 'point' }}
                    yScale={{ type: 'linear', min: 0, max: 'auto' }}
                    axisBottom={{
                      tickRotation: -45,
                      legend: 'Datum',
                      legendOffset: 45,
                      legendPosition: 'middle',
                    }}
                    axisLeft={{
                      format: (v) => `${v} €`,
                      legend: 'CPA-Wert / CPL kumuliert',
                      legendOffset: -50,
                      legendPosition: 'middle',
                    }}
                    enableArea
                    areaOpacity={0.2}
                    pointSize={6}
                    colors={[colors.greenAccent[400]]}
                    useMesh
                    tooltip={({ point }) =>
                      tooltipBox(`${point.data.xFormatted}`, eurFmt(Number(point.data.y) || 0))
                    }
                  />
                )}
              </Box>
            </Box>
          </Box>

          {/* Top assets + Mailings */}
          <Box
            display="grid"
            gridTemplateColumns={{ xs: '1fr', lg: '360px 1fr' }}
            gap="16px"
            mt={2}
          >
            <Box>
              <Typography variant="h5" sx={{ color: colors.grey[100], mb: 1, fontWeight: 600 }}>
                Top Opt-in Assets
              </Typography>
              <TableContainer component={Paper} sx={{ backgroundColor: '#ffffff', maxHeight: 420 }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ backgroundColor: '#e0e0e0 !important', fontWeight: 'bold' }}>
                        Asset
                      </TableCell>
                      <TableCell sx={{ backgroundColor: '#e0e0e0 !important', fontWeight: 'bold' }}>
                        ISIN
                      </TableCell>
                      <TableCell
                        align="right"
                        sx={{ backgroundColor: '#e0e0e0 !important', fontWeight: 'bold' }}
                      >
                        Abos
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {topAssets.map((a) => (
                      <TableRow key={a.isin} hover>
                        <TableCell sx={{ color: '#111' }}>{a.asset_name}</TableCell>
                        <TableCell sx={{ color: '#555', fontFamily: 'monospace', fontSize: 12 }}>
                          {a.isin}
                        </TableCell>
                        <TableCell align="right" sx={{ color: '#111', fontWeight: 600 }}>
                          {fmt(a.subscribers)}
                        </TableCell>
                      </TableRow>
                    ))}
                    {topAssets.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={3} sx={{ color: '#777' }}>
                          Keine Asset-Mappings.
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>

            <Box>
              <Typography variant="h5" sx={{ color: colors.grey[100], mb: 1, fontWeight: 600 }}>
                Kampagnen / Mailings
              </Typography>
              <TableContainer component={Paper} sx={{ backgroundColor: '#ffffff', maxHeight: 520 }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      {[
                        'Datum',
                        'Quelle',
                        'Titel',
                        'Status',
                        'Sends',
                        'Klicks',
                        'CTR',
                        'Leads/Best.',
                        'CPA-Wert / CPL',
                        'ETKP',
                        'Aktion',
                      ].map((h) => (
                        <TableCell
                          key={h}
                          sx={{ backgroundColor: '#e0e0e0 !important', fontWeight: 'bold' }}
                        >
                          {h}
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {mailings.map((m) => (
                      <TableRow key={m.id} hover>
                        <TableCell sx={{ color: '#111', whiteSpace: 'nowrap' }}>
                          {moment(m.scheduled_send_at || m.send_date).format('DD.MM.YYYY HH:mm')}
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={
                              m.source_type === 'webinar_followup'
                                ? 'WEBINAR NF'
                                : m.source_type.toUpperCase()
                            }
                            size="small"
                            color={
                              m.source_type === 'vnr'
                                ? 'primary'
                                : m.source_type === 'webinar'
                                  ? 'warning'
                                  : m.source_type === 'webinar_followup'
                                    ? 'info'
                                    : 'secondary'
                            }
                            variant="outlined"
                          />
                        </TableCell>
                        <TableCell sx={{ color: '#111', maxWidth: 280 }}>
                          <Tooltip title={m.body}>
                            <span>{m.title}</span>
                          </Tooltip>
                          {m.source_name && (
                            <Typography sx={{ color: '#888', fontSize: 11 }}>
                              {m.source_name}
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          <Chip label={m.status} size="small" color={STATUS_COLOR[m.status]} />
                          {m.status === 'failed' && m.error_message && (
                            <Tooltip title={m.error_message}>
                              <Typography sx={{ color: '#c00', fontSize: 10 }}>Details</Typography>
                            </Tooltip>
                          )}
                        </TableCell>
                        <TableCell sx={{ color: '#111' }}>{fmt(m.web_push_send)}</TableCell>
                        <TableCell sx={{ color: '#111' }}>{fmt(m.web_push_click)}</TableCell>
                        <TableCell sx={{ color: '#111' }}>{pct(m.ctr)}</TableCell>
                        <TableCell sx={{ color: '#111', fontWeight: 600 }}>
                          {fmt(m.conversions)}
                        </TableCell>
                        <TableCell sx={{ color: '#111', fontWeight: 600, whiteSpace: 'nowrap' }}>
                          {eurFmt(Number(m.revenue) || 0)}
                        </TableCell>
                        <TableCell sx={{ color: '#111', whiteSpace: 'nowrap' }}>
                          {m.web_push_send && m.web_push_send > 0
                            ? eurFmt(((Number(m.revenue) || 0) / m.web_push_send) * 1000)
                            : '–'}
                        </TableCell>
                        <TableCell sx={{ whiteSpace: 'nowrap' }}>
                          {m.status === 'draft' && (
                            <Tooltip title="Bearbeiten">
                              <IconButton size="small" onClick={() => openEdit(m)} disabled={busy}>
                                <EditOutlined fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                          {(m.status === 'draft' || m.status === 'scheduled') && (
                            <Tooltip title="Abbrechen">
                              <IconButton
                                size="small"
                                onClick={() => cancelMailing(m)}
                                disabled={busy}
                              >
                                <CancelOutlined fontSize="small" sx={{ color: '#c0392b' }} />
                              </IconButton>
                            </Tooltip>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                    {mailings.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={11} sx={{ color: '#777' }}>
                          {'Noch keine Mailings. „Entwurf generieren" startet den ersten.'}
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          </Box>
        </>
      )}

      {/* Edit draft modal */}
      <Dialog open={Boolean(editRow)} onClose={() => setEditRow(null)} maxWidth="sm" fullWidth>
        <DialogTitle>Entwurf bearbeiten #{editRow?.id}</DialogTitle>
        <DialogContent>
          <TextField
            label="Titel"
            fullWidth
            margin="normal"
            value={form.title}
            onChange={(e) => setForm({ ...form, title: e.target.value })}
            error={form.title.length > TITLE_MAX}
            helperText={`${form.title.length}/${TITLE_MAX}`}
          />
          <TextField
            label="Text"
            fullWidth
            multiline
            minRows={2}
            margin="normal"
            value={form.body}
            onChange={(e) => setForm({ ...form, body: e.target.value })}
            error={form.body.length > BODY_MAX}
            helperText={`${form.body.length}/${BODY_MAX}`}
          />
          <TextField
            label="Bild-URL"
            fullWidth
            margin="normal"
            value={form.image_url}
            onChange={(e) => setForm({ ...form, image_url: e.target.value })}
          />
          <TextField
            label="Klick-URL"
            fullWidth
            margin="normal"
            value={form.click_url}
            onChange={(e) => setForm({ ...form, click_url: e.target.value })}
          />
          {form.image_url && (
            <Box
              component="img"
              src={form.image_url}
              alt="Vorschau"
              sx={{ maxHeight: 90, mt: 1, borderRadius: 1 }}
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditRow(null)}>Abbrechen</Button>
          <Button
            variant="contained"
            onClick={saveEdit}
            disabled={
              busy ||
              !form.title.trim() ||
              !form.body.trim() ||
              form.title.length > TITLE_MAX ||
              form.body.length > BODY_MAX
            }
          >
            Speichern
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={Boolean(toast)}
        autoHideDuration={4000}
        onClose={() => setToast(null)}
        message={toast}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />
    </Box>
  );
};

export default WebPushDashboard;
